import { useQuery } from '@tanstack/react-query';
import apiService from '../services/api.service';
import type { CustomField } from '../components/CustomFieldBuilder';
import type { Slot } from '../components/volunteers/disponibilite';

export interface Volunteer {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  answers: Record<string, any> | null;
  role: string | null;
  available_slots: Slot[] | null;
  user_id: string | null;
  created_at: string;
}

export const ROLES = ['judge', 'staff', 'volunteer'] as const;
export type VolunteerRole = (typeof ROLES)[number];

/** Le formulaire tel que l'organisateur l'a défini, découpé par usage. */
export interface VolunteerForm {
  /** La question de rôle (éditée à part, elle a sa colonne). */
  roleField: CustomField | null;
  /** La question de disponibilité (idem). */
  availabilityField: CustomField | null;
  /** Les autres questions, dans l'ordre voulu : une colonne chacune. */
  freeFields: CustomField[];
}

export const volunteersQueryKey = (eventId: string | null) => ['volunteers', eventId] as const;
export const volunteerFormQueryKey = (eventId: string | null) => ['volunteer-form', eventId] as const;

export function useVolunteers(eventId: string | null) {
  return useQuery({
    queryKey: volunteersQueryKey(eventId),
    queryFn: async () => ((await apiService.get(`/events/${eventId}/volunteers`)).data || []) as Volunteer[],
    enabled: !!eventId,
    staleTime: 15 * 1000,
  });
}

export function useVolunteerForm(eventId: string | null) {
  return useQuery({
    queryKey: volunteerFormQueryKey(eventId),
    queryFn: async (): Promise<VolunteerForm> => {
      const fields = ((await apiService.get(`/events/${eventId}/volunteer-settings`)).data?.custom_fields || []) as CustomField[];
      const sorted = [...fields].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      return {
        roleField: sorted.find((f) => f.is_role) ?? null,
        availabilityField: sorted.find((f) => f.is_availability) ?? null,
        freeFields: sorted.filter((f) => !f.is_role && !f.is_availability),
      };
    },
    enabled: !!eventId,
    staleTime: 60 * 1000,
  });
}

/** Une réponse telle qu'on la lit dans une cellule ou un export. */
export function formatAnswer(value: unknown, yes: string, no: string): string {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'boolean') return value ? yes : no;
  if (Array.isArray(value)) return value.join(', ');
  return String(value);
}
